import { useCallback, useEffect, useRef, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { getMfaState, type MfaState } from './mfa.ts';

const EMPTY: MfaState = {
  enrolled: false,
  verifiedFactorId: null,
  factorType: null,
  atAal2: false,
  needsStepUp: false,
};

/**
 * The vendor's factor + assurance state, re-read whenever `revision` changes.
 *
 * `revision` is the access token (spec D8). That is what makes the gate react
 * to a token rotation: a step-up, or a refreshSession() after companyAuth
 * answered mfa_required, mints a new token, which re-runs this read, which
 * flips the gate decision. Keying on the token rather than a manual flag means
 * an admin's Reset 2FA also takes effect on the vendor's next refresh without
 * anything explicitly telling the SPA about it.
 *
 * Two deliberate differences from the driver-portal hook:
 *
 *  - `loading` is true only until the FIRST read resolves. Later re-reads keep
 *    the previous state on screen, so a token refresh does not blink the whole
 *    gate back to a spinner mid-session.
 *  - Errors surface in `error` instead of collapsing to an EMPTY state (D15).
 *    Collapsing would render "not enrolled" — i.e. the enrolment screen — to a
 *    vendor who is merely offline, and invite them to enrol a second factor on
 *    top of the one they already have.
 */
export function useMfa(
  client: SupabaseClient | null,
  revision: string | null,
): { state: MfaState; loading: boolean; error: Error | null; refresh: () => Promise<void> } {
  const [state, setState] = useState<MfaState>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  // A REF, not state. As state this was in the effect's dependency list, so
  // the first read flipped it false -> true, which re-fired the effect and
  // read again: four Supabase calls per mount instead of two, with the second
  // racing the first. A ref records the same fact without re-rendering.
  const settled = useRef(false);

  const read = useCallback(async () => {
    if (!client) {
      setLoading(false);
      return;
    }
    try {
      setState(await getMfaState(client));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      settled.current = true;
      setLoading(false);
    }
  }, [client]);

  useEffect(() => {
    // Only the FIRST read shows a spinner; later re-reads keep the previous
    // state on screen so a token refresh does not blink the gate.
    if (!settled.current) setLoading(true);
    void read();
    // `revision` is a dependency on purpose: a new access token means the
    // assurance level may have changed underneath us.
  }, [read, revision]);

  return { state, loading, error, refresh: read };
}
